import React, { useEffect, useMemo } from 'react'
import { Card, Col, Form, message, Row, Skeleton } from 'antd'
import { useQuery } from 'react-query'
import colors from 'components/apps/FinanceAccounts/Charts/colors'
import {
  getFinanceBankTransInputWhatsappTemplate,
  getFinanceInvoiceInputWhatsappTemplate,
  getFinanceOrderInputWhatsappTemplate,
  getFinancePurchaseInvoiceInputWhatsappTemplate,
  getFinancePurchaseOrderInputWhatsappTemplate,
  getFinancePurchaseQuoteInputWhatsappTemplate,
  getFinanceQuoteInputWhatsappTemplate,
} from 'utils/apis'
import { transType } from 'utils/static'
import WhatsAppPreview from 'components/apps/FinanceInvoices/FinanceInvoicesTable/WhatsAppPreview'
import { isEmpty } from 'lodash-es'
import styles from './official-whatsapp.module.scss'
import InputTemplate from './InputTemplate'

// ini adalah list alternative color yang ok (mudah dibaca) di dalam text
const alternativeColors = [colors[0], colors[12]]

const valueReplacementName = 'official_whatsapp_value_replacement'

export const useInputWhatsappTemplate = ({ id, transTypeId, options = {} }) => {
  const fetchFunction = useMemo(() => {
    switch (transTypeId) {
      case transType.INVOICE:
        return getFinanceInvoiceInputWhatsappTemplate
      case transType.ORDER:
        return getFinanceOrderInputWhatsappTemplate
      case transType.QUOTE:
        return getFinanceQuoteInputWhatsappTemplate
      case transType.PURCHASE_INVOICE:
        return getFinancePurchaseInvoiceInputWhatsappTemplate
      case transType.PURCHASE_ORDER:
        return getFinancePurchaseOrderInputWhatsappTemplate
      case transType.PURCHASE_QUOTE:
        return getFinancePurchaseQuoteInputWhatsappTemplate
      case transType.RECEIVE_INVOICE_PAYMENT:
        return getFinanceBankTransInputWhatsappTemplate
      default:
        return getFinanceInvoiceInputWhatsappTemplate
    }
  }, [transTypeId])

  return useQuery(
    ['input-whatsapp-template', id, transTypeId],
    async () => {
      const {
        data: { data },
      } = await fetchFunction(id)

      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

const OfficialWhatsapp = ({ id, transTypeId, usePreview }) => {
  const form = Form.useFormInstance()
  let watchValueReplacement = Form.useWatch(valueReplacementName, { form, preserve: true })
  watchValueReplacement = watchValueReplacement || []

  const { data, isLoading } = useInputWhatsappTemplate({ id, transTypeId })

  const defaultValues = useMemo(() => {
    if (!data?.default_value) {
      return []
    }

    return data.default_value.map((defaultValue, index) => {
      const colorIndex = index % colors.length

      const color = alternativeColors[colorIndex] || colors[colorIndex]

      return {
        text: defaultValue.replace(/\n /g, '\n'),
        color,
      }
    })
  }, [data])

  useEffect(() => {
    if (watchValueReplacement.length === 0 && defaultValues.length > 0) {
      const _newValues = defaultValues.map((defaultValue) => defaultValue.text)
      form.setFieldValue(valueReplacementName, _newValues)
    }
  }, [defaultValues, form, watchValueReplacement.length])

  const formatText = useMemo(() => {
    if (!data || isLoading || watchValueReplacement.length === 0) {
      return ''
    }

    const { content } = data.payload

    const parts = !isEmpty(content) ? content.split(/(\{\{\d+}})/) : []

    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)}}/)
      if (match) {
        const _value = parseInt(match[1], 10) - 1
        const defaultValue = defaultValues[_value]
        const replacementValue = watchValueReplacement[_value] || ''
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={index}>
            <span style={{ color: defaultValue?.color, fontWeight: 'bold' }}>
              {replacementValue}
            </span>
          </React.Fragment>
        )
      }

      // For non-placeholder text, split by newlines and add <br /> tags
      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {part.split('\n').map((line, lineIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${index}-${lineIndex}`}>
              {lineIndex > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </React.Fragment>
      )
    })
  }, [data, defaultValues, isLoading, watchValueReplacement])

  return (
    <Skeleton loading={isLoading} active>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          {defaultValues.map((defaultValue, index) => (
            <InputTemplate
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              defaultValue={defaultValue.text}
              value={watchValueReplacement[index]}
              backgroundColor={defaultValue.color}
              onChange={(newValue) => {
                const newValuesReplacement = [...watchValueReplacement]

                newValuesReplacement[index] = newValue

                form.setFieldValue(valueReplacementName, newValuesReplacement)
              }}
            />
          ))}
        </Col>
        <Col
          xs={24}
          md={12}
          className={usePreview ? 'd-flex flex-column justify-content-center' : ''}
        >
          {usePreview ? (
            <WhatsAppPreview>{formatText}</WhatsAppPreview>
          ) : (
            <Card
              type="inner"
              styles={{
                body: {
                  backgroundColor: '#DAD3CC',
                },
              }}
              title="Template Preview"
            >
              <div className={styles.chatBoxContainer}>
                <svg viewBox="0 0 8 13" height="13" width="8" className={styles.chatBoxLeftArrow}>
                  <path
                    d="M1.533 3.568L8 12.193V1H2.812C1.042 1 .474 2.156 1.533 3.568z"
                    fill="currentColor"
                  />
                </svg>
                {formatText}
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </Skeleton>
  )
}

export default OfficialWhatsapp
